import { useEffect, useRef, useState } from "react";
import {
  Plus,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  Search,
  Archive,
  Inbox,
  Undo2,
  Trash2,
} from "lucide-react";
import {
  effectiveRange,
  shortDate,
  todayIn,
  type Entity,
  type User,
} from "@kkindex/shared";
import { Checkbox, Drop, Links } from "./components";
import { TaskRow } from "./TaskRow";
import type { Execute } from "./Editor";
import { TaskCard } from "./TaskCard";
import { taskColumns } from "./taskLayout";
import { InlineTitle } from "./InlineTitle";
import s from "./App.module.css";
export function Tasks({
  items,
  user,
  execute,
  edit,
  addParent,
  draggingParent,
  draggingTask,
  mobile,
}: {
  items: Entity[];
  user: User;
  execute: Execute;
  edit: (e: Entity) => void;
  addParent: () => void;
  draggingParent: boolean;
  draggingTask: boolean;
  mobile: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(1);
  useEffect(() => {
    const element = scrollRef.current!;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width - 28;
      setColumnCount(
        mobile ? 1 : Math.max(1, Math.min(3, Math.floor((width + 12) / 252))),
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [mobile]);
  const [filter, setFilter] = useState("未完成"),
    [query, setQuery] = useState(""),
    [search, setSearch] = useState(false),
    [view, setView] = useState<"active" | "archive" | "trash">("active");
  const [folded, setFolded] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(`folded:${user.id}`) || "{}");
    } catch {
      return {};
    }
  });
  const [quick, setQuick] = useState<Record<string, string>>({}),
    [quickParent, setQuickParent] = useState<string | null>(null);
  const flip = (p: Entity) => {
    const next = { ...folded, [p.id]: !(folded[p.id] ?? p.done) };
    setFolded(next);
    localStorage.setItem(`folded:${user.id}`, JSON.stringify(next));
  };
  const toggle = (e: Entity) =>
    void execute(`/items/${e.id}`, "PATCH", {
      done: !e.done,
      revision: e.revision,
    }).catch(() => {});
  const match = (e: Entity) =>
    !query ||
    [e.title, e.note, ...e.links.map((l) => l.name)]
      .join(" ")
      .toLowerCase()
      .includes(query.toLowerCase());
  const stateMatch = (e: Entity) =>
    filter === "全部" ||
    (filter === "未完成" && !e.done) ||
    (filter === "已完成" && e.done) ||
    (filter === "未安排" && !e.start);
  const parents = items
    .filter(
      (e) =>
        e.kind === "parent" &&
        !e.deletedAt &&
        (view === "archive" ? e.archived : !e.archived),
    )
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  const today = todayIn(user.timezone);
  return (
    <aside className={s.tasks} aria-label="完整任务清单">
      <header className={s.panelHead}>
        <h2>
          {view === "active"
            ? "任务"
            : view === "archive"
              ? "已归档"
              : "回收站"}
        </h2>
        <div className={s.inline}>
          <button aria-label="搜索任务" onClick={() => setSearch(!search)}>
            <Search size={16} />
          </button>
          <button
            aria-label="查看归档"
            onClick={() => setView(view === "archive" ? "active" : "archive")}
          >
            <Archive size={16} />
          </button>
          <button
            aria-label="查看回收站"
            onClick={() => setView(view === "trash" ? "active" : "trash")}
          >
            <Trash2 size={16} />
          </button>
          <button aria-label="添加父任务" onClick={addParent}>
            <Plus size={18} />
          </button>
        </div>
      </header>
      {search && (
        <div className={s.taskSearch}>
          <input
            aria-label="任务搜索"
            placeholder="搜索任务、备注和链接"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      {view === "active" && (
        <nav className={s.filters}>
          {["全部", "未完成", "已完成", "未安排"].map((f) => (
            <button
              className={filter === f ? s.active : ""}
              key={f}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
        </nav>
      )}
      <div ref={scrollRef} className={s.taskScroll}>
        {view === "trash" ? (
          <>
            <p className={s.hint}>删除后保留 30 天</p>
            {items
              .filter(
                (e) =>
                  e.deletedAt &&
                  match(e) &&
                  (!e.parentId ||
                    !items.find((p) => p.id === e.parentId)?.deletedAt),
              )
              .map((e) => (
                <div className={s.trashRow} key={e.id}>
                  <span>
                    {e.title}
                    <small>{e.deletedAt!.slice(0, 10)}</small>
                  </span>
                  <button
                    aria-label={`恢复 ${e.title}`}
                    onClick={() =>
                      void execute(`/items/${e.id}/restore`, "POST", {
                        revision: e.revision,
                      }).catch(() => {})
                    }
                  >
                    <Undo2 size={17} />
                  </button>
                </div>
              ))}
          </>
        ) : (
          <div
            className={`${s.taskBoard} ${draggingParent ? s.boardDragging : ""}`}
            style={{
              gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
            }}
          >
            {taskColumns(parents, columnCount).map((columnParents, column) => (
              <div
                className={s.taskColumn}
                key={column}
                data-task-column={column}
              >
                {columnParents.map((p) => {
                  const children = items
                      .filter(
                        (t) =>
                          t.kind === "task" &&
                          t.parentId === p.id &&
                          !t.deletedAt,
                      )
                      .sort((a, b) => a.order - b.order),
                    done = children.filter((t) => t.done).length,
                    range = effectiveRange(p, children),
                    outside =
                      p.dateMode === "manual" &&
                      children.some(
                        (t) =>
                          t.start &&
                          range.start &&
                          range.end &&
                          (t.start < range.start || t.end! > range.end),
                      );
                  const visible = children.filter(
                    (t) =>
                      (view === "archive" || stateMatch(t)) &&
                      (match(t) || (!!query && match(p))),
                  );
                  if (!match(p) && !visible.length) return null;
                  if (
                    view === "active" &&
                    !p.builtin &&
                    !visible.length &&
                    !stateMatch(p)
                  )
                    return null;
                  const closed = folded[p.id] ?? p.done;
                  return (
                    <TaskCard
                      key={p.id}
                      item={p}
                      column={column}
                      columnCount={columnCount}
                      closed={closed}
                      draggingParent={draggingParent}
                      mobile={mobile}
                      execute={execute}
                      edit={edit}
                      header={
                        <>
                          <div className={s.parentRow}>
                            <button
                              aria-label={`${closed ? "展开" : "折叠"} ${p.title}`}
                              onClick={() => flip(p)}
                            >
                              {closed ? (
                                <ChevronRight size={14} />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </button>
                            <span className={s.dot} />
                            <InlineTitle
                              item={p}
                              selectionKey={`parent:${p.id}`}
                              execute={execute}
                              edit={edit}
                              readonly={p.builtin || p.archived}
                              className={s.parentTitle}
                            />
                            <span className={s.progress}>
                              {done}/{children.length}
                            </span>
                            <Links links={p.links} title={p.title} />
                            {!p.builtin && !p.archived && (
                              <Checkbox
                                done={p.done}
                                label={p.title}
                                onClick={() => toggle(p)}
                              />
                            )}
                            <button
                              aria-label={`编辑 ${p.title}`}
                              onClick={() => {
                                if (!p.archived) edit(p);
                              }}
                              disabled={p.archived}
                            >
                              <MoreHorizontal size={15} />
                            </button>
                          </div>
                          <div
                            className={`${s.groupMeta} ${outside ? s.error : ""}`}
                          >
                            {p.builtin ? (
                              <>
                                <Inbox size={11} />
                                随手记下，稍后安排
                              </>
                            ) : (
                              <>
                                {range.start
                                  ? `${shortDate(range.start)} – ${shortDate(range.end!)}`
                                  : "尚未安排"}{" "}
                                · {p.dateMode === "auto" ? "自动" : "手动"}
                                {outside ? " · 子任务超出计划范围" : ""}
                                <span className={s.progressTrack}>
                                  <i
                                    style={{
                                      width: `${children.length ? (done / children.length) * 100 : 0}%`,
                                    }}
                                  />
                                </span>
                              </>
                            )}
                          </div>
                        </>
                      }
                    >
                      {!closed &&
                        visible.map((t) => (
                          <TaskRow
                            key={t.id}
                            item={t}
                            draggingTask={draggingTask}
                            disabled={mobile || view !== "active"}
                            edit={() => {
                              if (!p.archived) edit(t);
                            }}
                          >
                            <Checkbox
                              done={t.done}
                              label={t.title}
                              onClick={() => !p.archived && toggle(t)}
                            />
                            <div className={s.taskBody}>
                              <InlineTitle
                                item={t}
                                selectionKey={`task:${t.id}`}
                                execute={execute}
                                edit={edit}
                                readonly={p.archived}
                              />
                              <small
                                className={
                                  !t.done && t.end && t.end < today
                                    ? s.error
                                    : ""
                                }
                              >
                                {t.start
                                  ? `${shortDate(t.start)}${t.end !== t.start ? " – " + shortDate(t.end!) : ""}`
                                  : "未安排"}
                              </small>
                            </div>
                            <Links links={t.links} title={t.title} />
                          </TaskRow>
                        ))}
                      {!closed &&
                        view === "active" &&
                        (quickParent === p.id ? (
                          <form
                            className={s.quick}
                            onSubmit={(e) => {
                              e.preventDefault();
                              const title = quick[p.id]?.trim();
                              if (title)
                                void execute("/items/task", "POST", {
                                  title,
                                  parentId: p.id,
                                  order: children.length,
                                })
                                  .then(() =>
                                    setQuick((v) => ({ ...v, [p.id]: "" })),
                                  )
                                  .catch(() => {});
                            }}
                          >
                            <input
                              autoFocus
                              aria-label={`在 ${p.title} 中添加子任务`}
                              value={quick[p.id] || ""}
                              onChange={(e) =>
                                setQuick((v) => ({
                                  ...v,
                                  [p.id]: e.target.value,
                                }))
                              }
                              placeholder="输入任务，Enter 连续添加"
                            />
                            <button aria-label="创建子任务">
                              <Plus size={15} />
                            </button>
                          </form>
                        ) : (
                          <button
                            className={s.addChild}
                            onClick={() => setQuickParent(p.id)}
                          >
                            <Plus size={13} />
                            添加子任务
                          </button>
                        ))}
                      {!p.builtin &&
                        (p.archived ? (
                          <button
                            className={s.addChild}
                            onClick={() =>
                              void execute(`/items/${p.id}`, "PATCH", {
                                archived: false,
                                revision: p.revision,
                              }).catch(() => {})
                            }
                          >
                            <Undo2 size={13} />
                            恢复归档
                          </button>
                        ) : (
                          p.done && (
                            <button
                              className={s.addChild}
                              disabled={done !== children.length}
                              title={
                                done !== children.length
                                  ? "所有子任务完成后才能归档"
                                  : ""
                              }
                              onClick={() =>
                                void execute(`/items/${p.id}`, "PATCH", {
                                  archived: true,
                                  revision: p.revision,
                                }).catch(() => {})
                              }
                            >
                              <Archive size={13} />
                              归档
                            </button>
                          )
                        ))}
                    </TaskCard>
                  );
                })}
                {view === "active" && (
                  <Drop
                    id={`card-column:${column}`}
                    accept={["parent"]}
                    label={`移到第 ${column + 1} 列末尾`}
                    data={{ surface: "card-layout", column, columnCount }}
                    className={s.columnEnd}
                  >
                    {draggingParent ? "放到这里" : ""}
                  </Drop>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
